import isEqual from 'fast-deep-equal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ActivityImagesDataType } from 'interfaces';
import type { AppDispatch } from 'store';
import { deleteActivityImage } from 'services/image-upload';
import { updateMoreOptionImageURLs } from 'store/reducer/modal/slice';
import { isActivityImageFilePathValid } from 'utils/activity-images.util';
import { v4 as uuidv4 } from 'uuid';

function cloneImageUrls(
  urls: ActivityImagesDataType[] | undefined
): ActivityImagesDataType[] {
  return (urls ?? []).map((item) => ({ ...item }));
}

type DraftImageRow = ActivityImagesDataType & { clientKey?: string };

function stripDraftRow(row: DraftImageRow): ActivityImagesDataType {
  const { clientKey: _clientKey, ...rest } = row;
  return rest;
}

function baselineFilePaths(rows: ActivityImagesDataType[]): Set<string> {
  return new Set(
    rows.map((r) => r.file_path).filter((p): p is string => Boolean(p))
  );
}

export function getDraftImageRowKey(row: DraftImageRow, index: number): string {
  if (row.clientKey) return row.clientKey;
  if (row.file_path) return `fp:${row.file_path}`;
  const trimmed = row.url?.trim();
  if (trimmed) return `url:${trimmed}@${index}`;
  return `idx:${index}`;
}

export interface ActivityInstructionImagesDraft {
  draftImageUrls: ActivityImagesDataType[];
  /** @deprecated Use draftImageUrls */
  mergedImageUrls: ActivityImagesDataType[];
  addDraftUrl: (url: string, dispatch: AppDispatch) => void;
  addDraftFile: (fileKey: string, dispatch: AppDispatch) => void;
  /** @deprecated Use addDraftFile */
  addPendingFile: (fileKey: string, dispatch: AppDispatch) => void;
  removeAtDraftIndex: (index: number, dispatch: AppDispatch) => void;
  /** @deprecated Use removeAtDraftIndex */
  removeAtMergedIndex: (
    index: number,
    dispatch: AppDispatch
  ) => Promise<void>;
  commitDraft: (dispatch: AppDispatch) => Promise<void>;
  discardDraft: (dispatch: AppDispatch) => void;
  isDraftAtMax: (max: number) => boolean;
  /** @deprecated Use isDraftAtMax */
  isMergedAtMax: (max: number) => boolean;
  getDraftRowKey: (index: number) => string;
}

export function useActivityInstructionImagesDraft({
  isActive,
  reduxImageUrls
}: {
  isActive: boolean;
  reduxImageUrls: ActivityImagesDataType[] | undefined;
}): ActivityInstructionImagesDraft {
  const baselineRef = useRef<ActivityImagesDataType[]>([]);
  const reduxRef = useRef(reduxImageUrls);
  reduxRef.current = reduxImageUrls;
  const syncingFromDraftRef = useRef(false);
  const orphanFilePathsRef = useRef<Set<string>>(new Set());

  const [draftImageUrls, setDraftImageUrls] = useState<DraftImageRow[]>([]);
  const draftRef = useRef(draftImageUrls);
  draftRef.current = draftImageUrls;

  const prevIsActiveRef = useRef(false);

  const syncDraftToRedux = useCallback(
    (dispatch: AppDispatch, draft: DraftImageRow[]) => {
      syncingFromDraftRef.current = true;
      dispatch(updateMoreOptionImageURLs(draft.map(stripDraftRow)));
      queueMicrotask(() => {
        syncingFromDraftRef.current = false;
      });
    },
    []
  );

  useEffect(() => {
    if (isActive && !prevIsActiveRef.current) {
      const baseline = cloneImageUrls(reduxRef.current);
      baselineRef.current = baseline;
      orphanFilePathsRef.current = new Set();
      setDraftImageUrls(baseline);
    }
    prevIsActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    if (!isActive || syncingFromDraftRef.current) return;

    const redux = cloneImageUrls(reduxImageUrls);
    setDraftImageUrls((prev) => {
      const prevStripped = prev.map(stripDraftRow);
      if (isEqual(prevStripped, redux)) {
        return prev;
      }
      return redux.map((row, i) => ({
        ...row,
        clientKey: prev[i]?.clientKey
      }));
    });
  }, [reduxImageUrls, isActive]);

  const setDraftAndSync = useCallback(
    (
      dispatch: AppDispatch,
      updater: (prev: DraftImageRow[]) => DraftImageRow[]
    ) => {
      setDraftImageUrls((prev) => {
        const next = updater(prev);
        syncDraftToRedux(dispatch, next);
        return next;
      });
    },
    [syncDraftToRedux]
  );

  const addDraftUrl = useCallback(
    (url: string, dispatch: AppDispatch) => {
      setDraftAndSync(dispatch, (prev) => [...prev, { url }]);
    },
    [setDraftAndSync]
  );

  const addDraftFile = useCallback(
    (fileKey: string, dispatch: AppDispatch) => {
      if (!isActivityImageFilePathValid(fileKey)) {
        return;
      }
      setDraftAndSync(dispatch, (prev) => [
        ...prev,
        { file_path: fileKey, clientKey: uuidv4() }
      ]);
    },
    [setDraftAndSync]
  );

  const removeAtDraftIndex = useCallback(
    (index: number, dispatch: AppDispatch) => {
      const baselinePaths = baselineFilePaths(baselineRef.current);
      const row = draftRef.current[index];
      if (row?.file_path && !baselinePaths.has(row.file_path)) {
        orphanFilePathsRef.current.add(row.file_path);
      }

      setDraftAndSync(dispatch, (prev) => prev.filter((_, i) => i !== index));
    },
    [setDraftAndSync]
  );

  const commitDraft = useCallback(
    async (dispatch: AppDispatch) => {
      const baseline = baselineRef.current;
      const draft = draftRef.current.map(stripDraftRow);
      const draftPaths = baselineFilePaths(draft);

      const removedFromBaseline = baseline
        .map((r) => r.file_path)
        .filter((p): p is string => Boolean(p && !draftPaths.has(p)));

      const orphanDeletes = [...orphanFilePathsRef.current];

      await Promise.all(
        [...removedFromBaseline, ...orphanDeletes].map((filePath) =>
          deleteActivityImage(filePath).catch(() => undefined)
        )
      );

      baselineRef.current = cloneImageUrls(draft);
      orphanFilePathsRef.current = new Set();
      syncDraftToRedux(dispatch, draftRef.current);
      setDraftImageUrls(cloneImageUrls(draft));
    },
    [syncDraftToRedux]
  );

  const discardDraft = useCallback((dispatch: AppDispatch) => {
    const baseline = baselineRef.current;
    const baselinePaths = baselineFilePaths(baseline);
    const draft = draftRef.current;

    const sessionUploadsStillInDraft = draft
      .map((r) => r.file_path)
      .filter((p): p is string => Boolean(p && !baselinePaths.has(p)));

    const toDelete = [
      ...new Set([...orphanFilePathsRef.current, ...sessionUploadsStillInDraft])
    ];

    dispatch(updateMoreOptionImageURLs(cloneImageUrls(baseline)));
    setDraftImageUrls(cloneImageUrls(baseline));
    orphanFilePathsRef.current = new Set();
    syncingFromDraftRef.current = true;
    queueMicrotask(() => {
      syncingFromDraftRef.current = false;
    });

    void Promise.all(
      toDelete.map((filePath) =>
        deleteActivityImage(filePath).catch(() => undefined)
      )
    );
  }, []);

  const isDraftAtMax = useCallback(
    (max: number) => draftImageUrls.length >= max,
    [draftImageUrls.length]
  );

  const getDraftRowKey = useCallback(
    (index: number) => {
      const row = draftImageUrls[index];
      return row ? getDraftImageRowKey(row, index) : `idx:${index}`;
    },
    [draftImageUrls]
  );

  const publicDraft = useMemo(
    () => draftImageUrls.map(stripDraftRow),
    [draftImageUrls]
  );

  return {
    draftImageUrls: publicDraft,
    mergedImageUrls: publicDraft,
    addDraftUrl,
    addDraftFile,
    addPendingFile: addDraftFile,
    removeAtDraftIndex,
    removeAtMergedIndex: async (index: number, dispatch: AppDispatch) => {
      removeAtDraftIndex(index, dispatch);
    },
    commitDraft,
    discardDraft,
    isDraftAtMax,
    isMergedAtMax: isDraftAtMax,
    getDraftRowKey
  };
}
